import { FormikProvider, useFormik } from 'formik';
import React, { memo, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { audioApi, pdfApi, wordApi, taskApi, videoApi } from 'api';
import { imgApi } from 'api/img-api';
import { Exam, TaskKeysType, TaskType, TestItem, TestTask as TestTaskType, WorkType } from 'shared/types';
import { testTaskSchema } from 'shared/types/task';
import { TaskName } from 'shared/ui';
import Clue from 'shared/ui/Clue';

import { TestItemForm, TestItemsList } from './ui';
import TaskWrapper from '../TaskWrapper';
import InputPoint from 'Components/InputPoint/InputPoint';

export type TestTaskProps = TestTaskType & {
  exam: Exam;
  handleDelete: (id: string) => void;
  userType: string;
  handleChangeTaskByKey: (id: string, key: TaskKeysType, value: string | boolean | string[]) => void;
  currentICLId: string;
  updateICL: boolean;
  workType?: WorkType;
  index: number;
  points?: number;
};

function TestTask({
  id,
  handleDelete,
  completedAt,
  text,
  subText,
  handleChangeTaskByKey,
  hasAnswer,
  hasStar,
  indexNumber,
  isActive,
  createdAt,
  type,
  currentICLId,
  updateICL,
  hasPhoto,
  deadline,
  audioUrl,
  videoUrl,
  pdfUrl,
  wordUrl,
  taskPhotos,
  answersPhotos,
  editedAnswerPhotos,
  workType,
  studentPdfUrl,
  studentWordUrl,
  testItems,
  exam,
  index,
}: TestTaskProps) {
  const { t } = useTranslation();
  const [taskId, setTaskId] = useState<string>('');

  const [isRemovingLoading, setIsRemovingLoading] = useState(false);

  const isExam = exam?.type === WorkType.exam;

  useEffect(() => {
    const init = async () => {
      try {
        const task = await taskApi.getTask(currentICLId, id);

        if (task) {
          setTaskId(task.id);
        }
      } catch (error) {
        console.log(error);
      }
    };

    init(); // to check if task exists in Firestore
  }, []);

  const initialFormValues = {
    text: updateICL ? text.replace(/<br>/g, '\n') : '',
    subText: updateICL ? subText?.replace(/<br>/g, '\n') : '',
    id,
    indexNumber,
    isActive,
    createdAt,
    type,
    hasAnswer,
    hasStar,
    hasPhoto,
    testItems,
  };

  const formValues = isExam ? { ...initialFormValues, points: 0 } : initialFormValues;

  const formik = useFormik<TestTaskType>({
    initialValues: formValues,
    onSubmit: async values => {
      const textWithLB = values.text.replace(/\n/g, '<br>');
      const subTextWithLB = values?.subText?.replace(/\n/g, '<br>');

      if (!taskId) {
        await taskApi
          .addTask<TestTaskType>(currentICLId, {
            ...values,
            indexNumber,
            text: textWithLB,
            subText: subTextWithLB,
            completedAt,
          })
          .then(id => {
            setTaskId(id);
            toast.success(t('Successfully added!'));

            return id;
          });
      } else {
        await taskApi.updateTask<TestTaskType>(currentICLId, {
          ...values,
          text: textWithLB,
          subText: subTextWithLB,
          completedAt,
        });
        toast.success(t('Successfully edited!'));
      }
    },
    validationSchema: testTaskSchema,
  });

  const deleteTask = async () => {
    if (!taskId) {
      handleDelete(id);
    }

    if (updateICL || taskId) {
      handleDelete(id);

      setIsRemovingLoading(true);
      try {
        wordUrl && (await wordApi.removeWord(wordUrl, currentICLId, id));
        pdfUrl && (await pdfApi.removePdf(pdfUrl, currentICLId, id));
        studentWordUrl && (await wordApi.removeWord(studentWordUrl, currentICLId, id, true));
        studentPdfUrl && (await pdfApi.removePdf(studentPdfUrl, currentICLId, id, true));
        videoUrl && (await videoApi.removeVideo(videoUrl, currentICLId, id));
        audioUrl && (await audioApi.removeAudio(audioUrl, currentICLId, id));
        taskPhotos &&
          taskPhotos.forEach(async photo => await imgApi.removeImg(photo, currentICLId, id, taskPhotos, 'taskPhotos'));
        answersPhotos &&
          answersPhotos.forEach(
            async photo => await imgApi.removeImg(photo, currentICLId, id, answersPhotos, 'answersPhotos'),
          );
        editedAnswerPhotos &&
          editedAnswerPhotos.forEach(
            async photo => await imgApi.removeImg(photo, currentICLId, id, editedAnswerPhotos, 'editedAnswerPhotos'),
          );
        await taskApi.removeTask<TestTaskType>(currentICLId, id);
      } finally {
        setIsRemovingLoading(false);
      }
    }
  };

  const handleChangeTaskByKeyWithFormik = (id: string, key: TaskKeysType, value: string | boolean | string[]) => {
    handleChangeTaskByKey(id, key, value);

    formik.setFieldValue(key, value);
  };

  return (
    <TaskWrapper
      id={id}
      handleDelete={deleteTask}
      completedAt={completedAt}
      handleChangeTaskByKey={handleChangeTaskByKeyWithFormik}
      hasAnswer={hasAnswer}
      hasStar={hasStar}
      save={formik.submitForm}
      formikProp={formik}
      updateICL={updateICL}
      isDeleteButtonDisabled={isRemovingLoading}
      hasPhoto={hasPhoto}
      deadline={deadline}
      isActive={isActive}
      currentICLId={currentICLId}
      audioUrl={audioUrl}
      remoteTaskId={taskId}
      videoUrl={videoUrl}
      pdfUrl={pdfUrl}
      wordUrl={wordUrl}
      taskPhotos={taskPhotos}
      workType={workType}
      studentPdfUrl={studentPdfUrl}
      studentWordUrl={studentWordUrl}
      index={index}
    >
      <FormikProvider value={formik}>
        <div className="flex flex-col !gap-4 lg:!gap-5">
          <TaskName type="test" />

          {isExam && (
            <InputPoint value={formik.values.points || 0} onChange={value => formik.setFieldValue('points', value)} />
          )}

          <div className="relative">
            <div className="text-[14px] font-bold uppercase lg:text-[18px]">{t('exercise heading')}:</div>

            <textarea
              value={formik.values.text}
              className={
                formik.errors.text
                  ? 'h-[70px] w-full border-b-2 border-none bg-[linear-gradient(to_right,_#f5f5f5_1px,_transparent_1px),_linear-gradient(to_left,_#f5f5f5_1px,_transparent_1px),_repeating-linear-gradient(#f5f5f5,_#f5f5f5_33px,_#FA6238_35px,_#97A0C3_31px,_#f5f5f5_35px)] bg-local pt-[8px] text-[18px] font-medium leading-[35px] placeholder:text-lg lg:text-[22px] lg:placeholder:text-[22px]'
                  : 'h-[70px] w-full border-b-2 border-none bg-[linear-gradient(to_right,_#f5f5f5_1px,_transparent_1px),_linear-gradient(to_left,_#f5f5f5_1px,_transparent_1px),_repeating-linear-gradient(#f5f5f5,_#f5f5f5_33px,_#242C2C_35px,_#97A0C3_31px,_#f5f5f5_35px)] bg-local pt-[8px] text-[18px] font-medium leading-[35px] placeholder:text-lg lg:text-[22px] lg:placeholder:text-[22px]'
              }
              onChange={event => formik.setFieldValue('text', event.target.value)}
              placeholder={t('enter task title')}
            />

            {!!formik.errors.text && (
              <p className="absolute left-0 top-14 !mb-0 inline-block h-fit text-sm font-medium text-red-600 lg:top-[60px]">
                {t(formik.errors.text as string)}
              </p>
            )}
          </div>

          <div className="relative">
            <div className="text-[14px] font-bold uppercase lg:text-[18px]">{t('exercise')}:</div>

            <textarea
              value={formik.values.subText}
              className={`h-[70px] w-full border-b-2 text-[18px] lg:text-[22px] ${formik.errors.subText ? 'border-red-600' : 'border-black'} border-none bg-[linear-gradient(to_right,_#f5f5f5_1px,_transparent_1px),_linear-gradient(to_left,_#f5f5f5_1px,_transparent_1px),_repeating-linear-gradient(#f5f5f5,_#f5f5f5_33px,_#242C2C_35px,_#97A0C3_31px,_#f5f5f5_35px)] bg-local pt-[8px] font-medium leading-[35px] placeholder:text-lg lg:placeholder:text-[22px]`}
              onChange={event => formik.setFieldValue('subText', event.target.value)}
              placeholder={t('enter task text')}
            />

            {!!formik.errors.subText && (
              <p className="absolute -bottom-4 left-0 !mb-0 inline-block h-fit text-xs text-red-600">
                {t(formik.errors.subText as string)}
              </p>
            )}
          </div>

          <Clue text="testHint" />
          {formik.values.testItems?.length > 0 && <TestItemsList testTaskFormik={formik} />}
          <TestItemForm testTaskFormik={formik} />
          <div className="relative">
            {!!formik.errors.testItems && (
              <p className="!mb-0 h-fit text-sm font-medium text-red-600 lg:top-[60px]">
                {t(formik.errors.testItems as string)}
              </p>
            )}
          </div>
        </div>
      </FormikProvider>
    </TaskWrapper>
  );
}

export default memo(TestTask);
